import {
  Query,
  SelectableOrExpressionOfType,
  SetQueryReturnsColumnOrThrow,
} from '../../query';
import {
  AggregateOptions,
  makeFnExpression,
  FnExpression,
  Over,
} from '../../expressions/fn-expression';
import {
  OperatorsAny,
  OperatorsArray,
  OperatorsBoolean,
  OperatorsNumber,
  OperatorsText,
} from '../../../columns/operators';
import { RawSql } from '../../expressions/raw-sql';
import {
  BooleanColumn,
  DecimalColumn,
  IntegerColumn,
  JSONTextColumn,
  NumberAsStringBaseColumn,
  NumberBaseColumn,
  RealColumn,
  TextColumn,
  XMLColumn,
} from '../../../columns';
import { Column } from '../../../columns/column';
import { defaultSchemaConfig } from '../../../columns/default-schema-config';
import {
  _getSelectableColumn,
  _queryGetOptional,
  QueryGetSelf,
} from '../get/get.utils';
import {
  Expression,
  ExpressionOutput,
  SelectableOrExpression,
} from '../../expressions/expression';
import {
  PickQuerySelectableResultRelationsWindows,
  PickQueryQ,
  PickQuerySelectable,
} from '../../pick-query-types';
import { emptyArray } from '../../../utils';
import { _clone } from '../clone/clone';
import { SearchAggregateMethods } from '../../extra-features/search/search';

// Helper function to check if we're selecting a count on this query.
// Used in `create` to not return a full record after `count()` method.
export const isSelectingCount = (q: PickQueryQ) => {
  const { expr } = q.q;
  return (
    expr instanceof FnExpression && expr.fn === 'count' && expr.args[0] === '*'
  );
};

// `count` returns `bigint` type that is represented by a string.
// This is needed to parse the value back to a number.
const intNullable = new IntegerColumn(defaultSchemaConfig)
  .nullable()
  .parse(parseInt as never);

// double-precision is represented by string in JS, parse it to float.
const floatNullable = new RealColumn(defaultSchemaConfig)
  .nullable()
  .parse(parseFloat as never);

const booleanNullable = BooleanColumn.instance.nullable();

const textNullable = TextColumn.instance.nullable();

const jsonTextNullable = JSONTextColumn.instance.nullable();

const xmlNullable = XMLColumn.instance.nullable();

const stringAsNumberNullable =
  new (NumberAsStringBaseColumn as unknown as typeof DecimalColumn)(
    defaultSchemaConfig,
  ).nullable();

const numericResultColumn = (
  q: unknown,
  arg: PropertyKey | Expression,
): Column => {
  const query = q as Query;

  const type =
    typeof arg === 'string'
      ? _getSelectableColumn(query, arg)
      : (arg as Expression).result.value;

  let column =
    type instanceof NumberBaseColumn ? floatNullable : stringAsNumberNullable;

  const parse = typeof arg === 'string' && query.q.parsers?.[arg];
  if (parse) column = column.parse(parse) as typeof column;
  return column;
};

interface OperatorsCount extends OperatorsNumber {
  isCount: true;
}

type CountColumn = Column.Pick.QueryColumnOfTypeAndOps<
  'int8',
  number,
  OperatorsCount
>;

type CountReturn<T> = SetQueryReturnsColumnOrThrow<T, CountColumn> &
  OperatorsCount;

type SelectableDataType<
  T extends PickQuerySelectable,
  DataType extends string,
> =
  | {
      [K in keyof T['__selectable']]: T['__selectable'][K]['column']['dataType'] extends DataType
        ? K
        : never;
    }[keyof T['__selectable']]
  | Expression<Column.Pick.QueryColumnOfDataType<DataType>>;

type NumericReturn<
  T extends PickQuerySelectable,
  Arg,
> = Arg extends keyof T['__selectable']
  ? SetQueryReturnsColumnOrThrow<
      T,
      Column.Pick.QueryColumnOfTypeAndOps<
        T['__selectable'][Arg]['column']['dataType'],
        T['__selectable'][Arg]['column']['type'] | null,
        OperatorsNumber
      >
    > &
      OperatorsNumber
  : Arg extends Expression
    ? SetQueryReturnsColumnOrThrow<
        T,
        Column.Pick.QueryColumnOfTypeAndOps<
          Arg['result']['value']['dataType'],
          Arg['result']['value']['type'] | null,
          OperatorsNumber
        >
      > &
        OperatorsNumber
    : never;

type NullableNumberReturn<T, DataType> = SetQueryReturnsColumnOrThrow<
  T,
  Column.Pick.QueryColumnOfTypeAndOps<DataType, number | null, OperatorsNumber>
> &
  OperatorsNumber;

export type BooleanQueryColumn = Column.Pick.QueryColumnOfTypeAndOps<
  'bool',
  boolean,
  OperatorsBoolean
>;

type BooleanNullable = Column.Pick.QueryColumnOfTypeAndOps<
  'bool',
  boolean | null,
  OperatorsBoolean
>;

type NullableBooleanReturn<T> = SetQueryReturnsColumnOrThrow<
  T,
  BooleanNullable
> &
  OperatorsBoolean;

type NullableJSONAggReturn<
  T extends PickQuerySelectable,
  Arg extends SelectableOrExpression<T>,
> = SetQueryReturnsColumnOrThrow<
  T,
  {
    dataType: 'json';
    type: (ExpressionOutput<T, Arg>['type'] | null)[] | null;
    outputType: (ExpressionOutput<T, Arg>['outputType'] | null)[] | null;
    queryType: (ExpressionOutput<T, Arg>['queryType'] | null)[] | null;
    operators: OperatorsArray<never>;
  }
> &
  OperatorsArray<never>;

interface RecordSelectableOrExpression<T extends PickQuerySelectable> {
  [K: string]: SelectableOrExpression<T>;
}

type NullableJSONObjectReturn<
  T extends PickQuerySelectable,
  Obj extends RecordSelectableOrExpression<T>,
> = SetQueryReturnsColumnOrThrow<
  T,
  {
    dataType: 'json';
    type:
      | {
          [K in keyof Obj]: ExpressionOutput<T, Obj[K]>['type'];
        }
      | null;
    outputType:
      | {
          [K in keyof Obj]: ExpressionOutput<T, Obj[K]>['outputType'];
        }
      | null;
    queryType:
      | {
          [K in keyof Obj]: ExpressionOutput<T, Obj[K]>['queryType'];
        }
      | null;
    operators: OperatorsAny;
  }
> &
  OperatorsAny;

type StringColumn = Column.Pick.QueryColumnOfTypeAndOps<
  string,
  string,
  OperatorsText
>;

type StringNullable = Column.Pick.QueryColumnOfTypeAndOps<
  string,
  string | null,
  OperatorsText
>;

type NullableStringReturn<T> = SetQueryReturnsColumnOrThrow<T, StringNullable> &
  OperatorsText;

export interface AggregateArgTypes {
  minMax: // bpchar, timetz not supported by ORM
    | 'citext'
    | 'date'
    | 'float4'
    | 'float8'
    | 'inet'
    | 'int2'
    | 'int4'
    | 'int8'
    | 'interval'
    | 'money'
    | 'numeric'
    | 'text'
    | 'time'
    | 'timestamp'
    | 'timestamptz';
  sum:
    | 'float4'
    | 'float8'
    | 'int2'
    | 'int4'
    | 'int8'
    | 'interval'
    | 'money'
    | 'numeric';
  avg: // unlike sum, avg has no money
    'float4' | 'float8' | 'int2' | 'int4' | 'int8' | 'interval' | 'numeric';
  bit: 'bit' | 'int2' | 'int4' | 'int8';
  bool: 'bool';
  stringAgg: 'bytea' | 'text';
}

export interface AggregateMethods extends SearchAggregateMethods {}

// Query methods to get a single value for an aggregate function
export class AggregateMethods {
  /**
   * Use `exists()` to check if there is at least one record-matching condition.
   *
   * It will discard previous `select` statements if any. Returns a boolean.
   *
   * ```ts
   * const exists: boolean = await db.table.where(...conditions).exists();
   * ```
   */
  exists<T extends QueryGetSelf>(
    this: T,
  ): SetQueryReturnsColumnOrThrow<T, BooleanQueryColumn> {
    const q = _queryGetOptional(_clone(this), new RawSql('true'));
    q.q.notFoundDefault = false;
    q.q.coalesceValue = new RawSql('false');
    return q as never;
  }

  /**
   * Count records with the `count` function:
   *
   * ```ts
   * // count all records:
   * const result: number = await db.table.count();
   *
   * // count records where a column is not NULL:
   * db.table.count('name');
   *
   * // see options above:
   * db.table.count('*', aggregateOptions);
   *
   * // select counts of people grouped by city
   * db.people
   *   .select('city', {
   *     population: (q) => q.count(),
   *   })
   *   .group('city');
   * ```
   *
   * @param arg - optionally, provide a column or a raw SQL for the `count` argument
   * @param options - aggregation options
   */
  count<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    arg: SelectableOrExpression<T> = '*',
    options?: AggregateOptions<T>,
  ): CountReturn<T> {
    return makeFnExpression(
      this,
      intNullable,
      'count',
      [arg],
      options,
    ) as never;
  }

  /**
   * Get the minimum value for the specified numeric column, returns number or `null` if there are no records.
   *
   * ```ts
   * const result: number | null = await db.table.min(
   *   'numericColumn',
   *   aggregateOptions,
   * );
   *
   * // select min product price grouped by product category
   * db.product
   *   .select('category', {
   *     minPrice: (q) => q.min('price'),
   *   })
   *   .group('category')
   *   .take();
   * ```
   *
   * @param arg - numeric column or raw SQL
   * @param options - aggregation options
   */
  min<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableDataType<T, AggregateArgTypes['minMax']>,
  >(this: T, arg: Arg, options?: AggregateOptions<T>): NumericReturn<T, Arg> {
    return makeFnExpression(
      this,
      numericResultColumn(this, arg),
      'min',
      [arg],
      options,
    ) as never;
  }

  /**
   * Gets the maximum value for the specified numeric column, returns number or `null` if there are no records.
   *
   * ```ts
   * const result: number | null = await db.table.max(
   *   'numericColumn',
   *   aggregateOptions,
   * );
   *
   * // select max product price grouped by product category
   * db.product
   *   .select('category', {
   *     maxPrice: (q) => q.max('price'),
   *   })
   *   .group('category')
   *   .take();
   * ```
   *
   * @param arg - numeric column or raw SQL
   * @param options - aggregation options
   */
  max<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableDataType<T, AggregateArgTypes['minMax']>,
  >(this: T, arg: Arg, options?: AggregateOptions<T>): NumericReturn<T, Arg> {
    return makeFnExpression(
      this,
      numericResultColumn(this, arg),
      'max',
      [arg],
      options,
    ) as never;
  }

  /**
   * Retrieve the sum of the values of a given numeric column, returns number or `null` if there are no records.
   *
   * ```ts
   * const result: number | null = await db.table.sum(
   *   'numericColumn',
   *   aggregateOptions,
   * );
   *
   * // select sum of employee salaries grouped by years
   * db.employee
   *   .select('year', {
   *     yearlySalaries: (q) => q.sum('salary'),
   *   })
   *   .group('year');
   * ```
   *
   * @param arg - numeric column or raw SQL
   * @param options - aggregation options
   */
  sum<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableDataType<T, AggregateArgTypes['sum']>,
  >(this: T, arg: Arg, options?: AggregateOptions<T>): NumericReturn<T, Arg> {
    return makeFnExpression(
      this,
      numericResultColumn(this, arg),
      'sum',
      [arg],
      options,
    ) as never;
  }

  /**
   * Retrieve the average value of a numeric column, it returns a number or `null` if there are no records.
   *
   * ```ts
   * const result: number | null = db.table.avg('numericColumn', aggregateOptions);
   *
   * // select average movies ratings
   * db.movie
   *   .select('title', {
   *     averageRating: (q) => q.avg('rating'),
   *   })
   *   .group('title');
   * ```
   *
   * @param arg - numeric column or raw SQL
   * @param options - aggregation options
   */
  avg<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableDataType<T, AggregateArgTypes['avg']>,
  >(this: T, arg: Arg, options?: AggregateOptions<T>): NumericReturn<T, Arg> {
    return makeFnExpression(
      this,
      numericResultColumn(this, arg),
      'avg',
      [arg],
      options,
    ) as never;
  }

  /**
   * Bitwise `and` aggregation, returns `number` or `null` if there are no records.
   *
   * ```ts
   * const result: number | null = db.table.bitAnd(
   *   'numericColumn',
   *   aggregateOptions,
   * );
   *
   * // select grouped `bitAnd`
   * db.table
   *   .select('someColumn', {
   *     bitAnd: (q) => q.bitAnd('numericColumn'),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - numeric column or raw SQL
   * @param options - aggregation options
   */
  bitAnd<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableDataType<T, AggregateArgTypes['bit']>,
  >(this: T, arg: Arg, options?: AggregateOptions<T>): NumericReturn<T, Arg> {
    return makeFnExpression(
      this,
      numericResultColumn(this, arg),
      'bit_and',
      [arg],
      options,
    ) as never;
  }

  /**
   * Bitwise `or` aggregation, returns `number` or `null` if there are no records.
   *
   * ```ts
   * const result: number | null = db.table.bitOr('numericColumn', aggregateOptions);
   *
   * // select grouped `bitOr`
   * db.table
   *   .select('someColumn', {
   *     bitOr: (q) => q.bitOr('numericColumn'),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - numeric column or raw SQL
   * @param options - aggregation options
   */
  bitOr<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableDataType<T, AggregateArgTypes['bit']>,
  >(this: T, arg: Arg, options?: AggregateOptions<T>): NumericReturn<T, Arg> {
    return makeFnExpression(
      this,
      numericResultColumn(this, arg),
      'bit_or',
      [arg],
      options,
    ) as never;
  }

  /**
   * Aggregate booleans with `and` logic, it returns `boolean` or `null` if there are no records.
   *
   * ```ts
   * const result: boolean | null = db.table.boolAnd(
   *   'booleanColumn',
   *   aggregateOptions,
   * );
   *
   * // select grouped `boolAnd`
   * db.table
   *   .select('someColumn', {
   *     boolAnd: (q) => q.boolAnd('booleanColumn'),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - boolean column or raw SQL
   * @param options - aggregation options
   */
  boolAnd<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    arg: SelectableDataType<T, AggregateArgTypes['bool']>,
    options?: AggregateOptions<T>,
  ): NullableBooleanReturn<T> {
    return makeFnExpression(
      this,
      booleanNullable,
      'bool_and',
      [arg],
      options,
    ) as never;
  }

  /**
   * Aggregate booleans with `or` logic, it returns `boolean` or `null` if there are no records.
   *
   * ```ts
   * const result: boolean | null = db.table.boolOr(
   *   'booleanColumn',
   *   aggregateOptions,
   * );
   *
   * // select grouped `boolOr`
   * db.table
   *   .select('someColumn', {
   *     boolOr: (q) => q.boolOr('booleanColumn'),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - boolean column or raw SQL
   * @param options - aggregation options
   */
  boolOr<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    arg: SelectableDataType<T, AggregateArgTypes['bool']>,
    options?: AggregateOptions<T>,
  ): NullableBooleanReturn<T> {
    return makeFnExpression(
      this,
      booleanNullable,
      'bool_or',
      [arg],
      options,
    ) as never;
  }

  /**
   * Equivalent to {@link boolAnd}
   */
  every<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    arg: SelectableDataType<T, AggregateArgTypes['bool']>,
    options?: AggregateOptions<T>,
  ): NullableBooleanReturn<T> {
    return makeFnExpression(
      this,
      booleanNullable,
      'every',
      [arg],
      options,
    ) as never;
  }

  /**
   * Aggregate values into an array by using `json_agg`. Returns array of values or `null` if there are no records.
   *
   * `jsonAgg` is working a bit faster, `jsonbAgg` is better only when applying JSON operations in SQL.
   *
   * ```ts
   * const idsOrNull: number[] | null = db.table.jsonAgg('id', aggregateOptions);
   *
   * const namesOrNull: string[] | null = db.table.jsonbAgg(
   *   'name',
   *   aggregateOptions,
   * );
   *
   * // select grouped `jsonAgg`
   * db.table
   *   .select('someColumn', {
   *     jsonAgg: (q) => q.jsonAgg('anyColumn'),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - any column or raw SQL
   * @param options - aggregation options
   */
  jsonAgg<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableOrExpression<T>,
  >(
    this: T,
    arg: Arg,
    options?: AggregateOptions<T>,
  ): NullableJSONAggReturn<T, Arg> {
    return makeFnExpression(
      this,
      jsonTextNullable,
      'json_agg',
      [arg],
      options,
    ) as never;
  }

  /**
   * See {@link jsonAgg}
   */
  jsonbAgg<
    T extends PickQuerySelectableResultRelationsWindows,
    Arg extends SelectableOrExpression<T>,
  >(
    this: T,
    arg: Arg,
    options?: AggregateOptions<T>,
  ): NullableJSONAggReturn<T, Arg> {
    return makeFnExpression(
      this,
      jsonTextNullable,
      'jsonb_agg',
      [arg],
      options,
    ) as never;
  }

  /**
   * It does the construction of JSON objects, keys are provided strings and values can be table columns or raw SQL expressions, and returns `object` or `null` if no records.
   *
   * `jsonObjectAgg` is different from `jsonbObjectAgg` by internal representation in the database, `jsonObjectAgg` is a bit faster as it constructs a simple string.
   *
   * ```ts
   * import { TextColumn } from './string';
   *
   * // object has type { nameAlias: string, foo: string } | null
   * const object = await db.table.jsonObjectAgg(
   *   {
   *     // select a column with alias
   *     nameAlias: 'name',
   *     // select raw SQL with alias
   *     foo: sql<string>`"bar" || "baz"`,
   *   },
   *   aggregateOptions,
   * );
   *
   * // select aggregated object
   * db.table.select('id', {
   *   object: (q) =>
   *     q.jsonObjectAgg({
   *       nameAlias: 'name',
   *       foo: sql<string>`"bar" || "baz"`,
   *     }),
   * });
   * ```
   *
   * @param arg - object where values are column names or SQL
   * @param options - aggregation options
   */
  jsonObjectAgg<
    T extends PickQuerySelectableResultRelationsWindows,
    Obj extends RecordSelectableOrExpression<T>,
  >(
    this: T,
    arg: Obj,
    options?: AggregateOptions<T>,
  ): NullableJSONObjectReturn<T, Obj> {
    return makeFnExpression(
      this,
      jsonTextNullable,
      'json_object_agg',
      [{ pairs: arg }],
      options,
    ) as never;
  }

  /**
   * See {@link jsonObjectAgg}
   */
  jsonbObjectAgg<
    T extends PickQuerySelectableResultRelationsWindows,
    Obj extends RecordSelectableOrExpression<T>,
  >(
    this: T,
    arg: Obj,
    options?: AggregateOptions<T>,
  ): NullableJSONObjectReturn<T, Obj> {
    return makeFnExpression(
      this,
      jsonTextNullable,
      'jsonb_object_agg',
      [{ pairs: arg }],
      options,
    ) as never;
  }

  /**
   * Select joined strings, it returns a string or `null` if no records.
   *
   * ```ts
   * const result: string | null = db.table.stringAgg(
   *   'name',
   *   ', ',
   *   aggregateOptions,
   * );
   *
   * // select joined strings grouped by some column
   * db.table
   *   .select('someColumn', {
   *     joinedNames: (q) => q.stringAgg('name', ', '),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - string column or SQL
   * @param delimiter - string to join with
   * @param options - aggration options
   */
  stringAgg<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    arg: SelectableDataType<T, AggregateArgTypes['stringAgg']>,
    delimiter: string,
    options?: AggregateOptions<T>,
  ): NullableStringReturn<T> {
    return makeFnExpression(
      this,
      textNullable,
      'string_agg',
      [arg, { value: delimiter }],
      options,
    ) as never;
  }

  /**
   * Concatenates `xml` columns, returns a `string` or `null` if no records.
   *
   * ```ts
   * const xml: string | null = await db.table.xmlAgg('xmlColumn', aggregateOptions);
   *
   * // select joined XMLs grouped by some column
   * db.table
   *   .select('someColumn', {
   *     joinedXMLs: (q) => q.xmlAgg('xml'),
   *   })
   *   .group('someColumn');
   * ```
   *
   * @param arg - column or SQL with XML
   * @param options - aggregation options
   */
  xmlAgg<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    arg: SelectableOrExpressionOfType<T, StringColumn>,
    options?: AggregateOptions<T>,
  ): NullableStringReturn<T> {
    return makeFnExpression(
      this,
      xmlNullable,
      'xmlagg',
      [arg],
      options,
    ) as never;
  }

  /**
   * Selects the` row_number` window function.
   *
   * Returns the number of the current row within its partition, counting from 1.
   *
   * ```ts
   * // result is of type Array<{ rowNumber: number }>
   * const result = await db.table.select({
   *   rowNumber: (q) =>
   *     q.rowNumber({
   *       partitionBy: 'someColumn',
   *       order: { createdAt: 'ASC' },
   *     }),
   * });
   * ```
   *
   * @param over - OVER clause config
   */
  rowNumber<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    over?: Over<T>,
  ): NullableNumberReturn<T, 'int8'> {
    return makeFnExpression(this, intNullable, 'row_number', emptyArray, {
      over,
    }) as never;
  }

  /**
   * Selects the` rank` window function.
   *
   * Returns the rank of the current row, with gaps; that is, the row_number of the first row in its peer group.
   *
   * ```ts
   * // result is of type Array<{ rank: number }>
   * const result = await db.table.select({
   *   rank: (q) =>
   *     q.rank({
   *       partitionBy: 'someColumn',
   *       order: { createdAt: 'ASC' },
   *     }),
   * });
   * ```
   *
   * @param over - OVER clause config
   */
  rank<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    over?: Over<T>,
  ): NullableNumberReturn<T, 'int8'> {
    return makeFnExpression(this, intNullable, 'rank', emptyArray, {
      over,
    }) as never;
  }

  /**
   * Selects the` dense_rank` window function.
   *
   * Returns the rank of the current row, without gaps; this function effectively counts peer groups.
   *
   * ```ts
   * // result is of type Array<{ denseRank: number }>
   * const result = await db.table.select({
   *   denseRank: (q) =>
   *     q.denseRank({
   *       partitionBy: 'someColumn',
   *       order: { createdAt: 'ASC' },
   *     }),
   * });
   * ```
   *
   * @param over - OVER clause config
   */
  denseRank<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    over?: Over<T>,
  ): NullableNumberReturn<T, 'int8'> {
    return makeFnExpression(this, intNullable, 'dense_rank', emptyArray, {
      over,
    }) as never;
  }

  /**
   * Selects the `percent_rank` window function.
   *
   * Returns the relative rank of the current row, that is (rank - 1) / (total partition rows - 1). The value thus ranges from 0 to 1 inclusive.
   *
   * ```ts
   * // result is of type Array<{ percentRank: number }>
   * const result = await db.table.select({
   *   percentRank: (q) =>
   *     q.percentRank({
   *       partitionBy: 'someColumn',
   *       order: { createdAt: 'ASC' },
   *     }),
   * });
   * ```
   *
   * @param over - OVER clause config
   */
  percentRank<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    over?: Over<T>,
  ): NullableNumberReturn<T, 'float8'> {
    return makeFnExpression(this, intNullable, 'percent_rank', emptyArray, {
      over,
    }) as never;
  }

  /**
   * Selects the `cume_dist` window function.
   *
   * Returns the cumulative distribution, that is (number of partition rows preceding or peers with current row) / (total partition rows). The value thus ranges from 1/N to 1.
   *
   * ```ts
   * // result is of type Array<{ cumeDist: number }>
   * const result = await db.table.select({
   *   cumeDist: (q) =>
   *     q.cumeDist({
   *       partitionBy: 'someColumn',
   *       order: { createdAt: 'ASC' },
   *     }),
   * });
   * ```
   *
   * @param over - OVER clause config
   */
  cumeDist<T extends PickQuerySelectableResultRelationsWindows>(
    this: T,
    over?: Over<T>,
  ): NullableNumberReturn<T, 'float8'> {
    return makeFnExpression(this, floatNullable, 'cume_dist', emptyArray, {
      over,
    }) as never;
  }
}
